/**
 * The Collaborative Browser's live view (ADR-0137): one WebSocket per visible
 * panel, separate from the RPC socket so domain events never queue behind
 * frames. The server sends each frame as one binary message and every other
 * signal as JSON text; the client sends JSON text.
 *
 * @module BrowserView
 */
import * as Schema from "effect/Schema";

import { ThreadId, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { PreviewTabId } from "./preview.ts";

export const BROWSER_VIEW_WEBSOCKET_PATH = "/api/browser/view";

/**
 * A Cloud Workspace's Cloud Display as one RFB (VNC) stream, relayed unchanged
 * between the agent task and the screen panel, both ways.
 */
export const DISPLAY_VIEW_WEBSOCKET_PATH = "/api/display/view";

export const BrowserViewQuery = Schema.Struct({
  threadId: ThreadId,
  tabId: PreviewTabId,
  /** Distinguishes this panel from the others watching, so its own input is not echoed back. */
  viewerId: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
});
export type BrowserViewQuery = typeof BrowserViewQuery.Type;

const Coordinate = Schema.Finite;
const Modifiers = Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 15 }));

export const BrowserViewerInput = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("mouse"),
    type: Schema.Literals(["mousePressed", "mouseReleased", "mouseMoved"]),
    x: Coordinate,
    y: Coordinate,
    button: Schema.Literals(["none", "left", "middle", "right"]),
    clickCount: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 3 })),
    modifiers: Modifiers,
  }),
  Schema.Struct({
    kind: Schema.Literal("wheel"),
    x: Coordinate,
    y: Coordinate,
    deltaX: Coordinate,
    deltaY: Coordinate,
    modifiers: Modifiers,
  }),
  Schema.Struct({
    kind: Schema.Literal("key"),
    type: Schema.Literals(["keyDown", "keyUp", "rawKeyDown", "char"]),
    key: Schema.String.check(Schema.isMaxLength(64)),
    code: Schema.String.check(Schema.isMaxLength(64)),
    text: Schema.optional(Schema.String.check(Schema.isMaxLength(16))),
    windowsVirtualKeyCode: Schema.Int,
    modifiers: Modifiers,
  }),
  Schema.Struct({
    kind: Schema.Literal("insertText"),
    text: Schema.String.check(Schema.isMaxLength(10_000)),
  }),
]);
export type BrowserViewerInput = typeof BrowserViewerInput.Type;

export const BrowserViewClientMessage = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("resize"),
    width: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 7680 })),
    height: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 4320 })),
  }),
  Schema.Struct({ type: Schema.Literal("input"), event: BrowserViewerInput }),
]);
export type BrowserViewClientMessage = typeof BrowserViewClientMessage.Type;

export const BrowserViewSignal = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("cursor"),
    phase: Schema.Literals(["move", "click"]),
    x: Schema.Finite,
    y: Schema.Finite,
  }),
  Schema.Struct({
    type: Schema.Literal("controller"),
    controller: Schema.Literals(["agent", "none"]),
  }),
  /** Someone else is giving input to the page this panel shows. */
  Schema.Struct({ type: Schema.Literal("input"), viewerId: Schema.String }),
  /** The tab is gone; the client stops watching. */
  Schema.Struct({ type: Schema.Literal("closed"), reason: Schema.String }),
  /** The page's own icon, rasterized small, for tab strips. */
  Schema.Struct({
    type: Schema.Literal("favicon"),
    pageUrl: Schema.String,
    dataUrl: Schema.String,
    capturedAt: Schema.Number,
  }),
]);
export type BrowserViewSignal = typeof BrowserViewSignal.Type;

/** What precedes each frame's JPEG bytes. */
export const BrowserViewFrameHeader = Schema.Struct({
  width: Schema.Int,
  height: Schema.Int,
  /** The page's CSS viewport the frame shows, so a client maps input back to it. */
  deviceWidth: Schema.Finite,
  deviceHeight: Schema.Finite,
  pageScaleFactor: Schema.Finite,
});
export type BrowserViewFrameHeader = typeof BrowserViewFrameHeader.Type;
